import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";
import { LogoWordmark } from "@/components/Logo";
import { api } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e2) {
      // El backend siempre devuelve 200 con un mensaje genérico para no
      // filtrar si el email existe. Si llega aquí es un fallo de red real.
      setErr(e2?.response?.data?.detail || "No se pudo contactar con el servidor. Inténtalo en unos minutos.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="px-6 sm:px-10 py-6">
        <Link
          to="/login"
          data-testid="forgot-back-link"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" weight="bold" /> Volver al acceso
        </Link>
      </div>

      <div className="flex-1 flex items-start sm:items-center justify-center px-6 pb-16">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center gap-2 mb-10">
            <LogoWordmark />
          </div>

          {sent ? (
            <div data-testid="forgot-sent-state" className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-emerald-600" weight="fill" />
              </div>
              <h1 className="font-display font-black text-xl tracking-tight text-zinc-900">
                Revisa tu correo
              </h1>
              <p className="text-sm text-zinc-600 mt-3 leading-relaxed">
                Si la cuenta existe te hemos enviado un enlace de recuperación a
                <br />
                <strong className="text-zinc-900">{email}</strong>.
              </p>
              <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
                El enlace caduca en 30 minutos. Si no lo recibes, revisa la
                carpeta de spam o solicita uno nuevo.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="forgot-send-again"
                className="text-xs font-semibold text-brand hover:underline mt-5"
              >
                Pedir otro enlace
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-zinc-900">
                  Recupera tu contraseña
                </h1>
                <p className="text-xs text-zinc-500 mt-1.5">
                  Te enviaremos un enlace para crear una nueva.
                </p>
              </div>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
                <div>
                  <label htmlFor="email" className="!normal-case !tracking-normal !text-[11px] !font-semibold !text-zinc-600">
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <EnvelopeSimple className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      placeholder="tu@gabinete.es"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                      className="!text-sm !pl-9"
                    />
                  </div>
                </div>

                {err && (
                  <div
                    className="text-xs px-3 py-2 rounded-md bg-rose-50 text-rose-700 border border-rose-200"
                    data-testid="forgot-error"
                  >
                    {err}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  data-testid="forgot-submit-btn"
                  className="btn-brand w-full rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loading ? "Enviando..." : (
                    <>Enviar enlace <ArrowRight className="w-4 h-4" weight="bold" /></>
                  )}
                </button>
              </form>

              <p className="text-[11px] text-zinc-500 mt-6 text-center">
                ¿Recordaste tu contraseña?{" "}
                <Link to="/login" className="font-semibold text-brand hover:underline">
                  Inicia sesión
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
